import React, { useState, useRef, useEffect } from 'react';
import {
  Fab, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, ToggleButtonGroup, ToggleButton, Snackbar, Alert,
  Drawer, IconButton, CircularProgress, Collapse, Divider, Chip, Stack,
  Box, Typography, FormControlLabel, Switch,
} from '@mui/material';
import { Feedback, Close, Send, ExpandMore, ExpandLess } from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MAX_TITLE_LENGTH = 255;

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export default function FeedbackButton() {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<'feature' | 'bug'>('feature');
  const [autoFix, setAutoFix] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [showRefine, setShowRefine] = useState(false);
  const navHistoryRef = useRef<string[]>([]);

  // Design session state
  const [designOpen, setDesignOpen] = useState(false);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionStarting, setSessionStarting] = useState(false);
  const [userStory, setUserStory] = useState<string | null>(null);
  const [turnCount, setTurnCount] = useState(0);
  const [sessionStatus, setSessionStatus] = useState('active');
  const [storyExpanded, setStoryExpanded] = useState(true);
  const [designError, setDesignError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const lastFeedbackRef = useRef<{ type: string; title: string; description: string } | null>(null);

  const titleTooLong = title.length > MAX_TITLE_LENGTH;
  const titleApproachingLimit = title.length > 200;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  useEffect(() => {
    navHistoryRef.current = [
      location.pathname,
      ...navHistoryRef.current.filter((p) => p !== location.pathname),
    ].slice(0, 5);
  }, [location.pathname]);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/api/v1/feedback/', {
        type,
        title: title.trim(),
        description: description.trim() || null,
        page: location.pathname,
        user_context: {
          navigation_history: navHistoryRef.current,
          browser_info: navigator.userAgent,
          window_size: `${window.innerWidth}x${window.innerHeight}`,
        },
        auto_fix: autoFix,
      });
      lastFeedbackRef.current = { type, title: title.trim(), description: description.trim() };
      setShowRefine(true);
      setSnackbar(autoFix ? 'Submitted — auto-fix queued for Claude Code' : 'Feedback submitted — thanks!');
      setOpen(false);
      setTitle('');
      setDescription('');
      setType('feature');
      setAutoFix(false);
    } catch {
      setSnackbar('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefineWithAI = async () => {
    if (!lastFeedbackRef.current) return;
    setShowRefine(false);
    setSnackbar(null);
    setDesignOpen(true);
    setSessionStarting(true);
    setDesignError(null);
    setMessages([]);
    setSessionId(null);
    setUserStory(null);
    setTurnCount(0);
    setSessionStatus('active');

    try {
      const fb = lastFeedbackRef.current;
      const res = await api.post('/api/v1/design-sessions/start', {
        feedback_ref: `qt-${Date.now()}`,
        feedback_type: fb.type,
        title: fb.title,
        description: fb.description,
      });
      const session = res.data;
      setSessionId(session.id);
      setSessionStatus(session.status);
      setTurnCount(session.turn_count || 0);
      setUserStory(session.user_story || null);
      const msgs: ChatMessage[] = (session.messages || [])
        .filter((m: ChatMessage) => m.role === 'user' || m.role === 'assistant')
        .map((m: ChatMessage) => ({ role: m.role, content: m.content }));
      setMessages(msgs);
    } catch (err: any) {
      setDesignError(err.response?.data?.detail || 'Failed to start design session');
    } finally {
      setSessionStarting(false);
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || !sessionId || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    setDesignError(null);

    try {
      const res = await api.post(`/api/v1/design-sessions/${sessionId}/message`, { content: userMsg });
      const data = res.data;
      setMessages((prev) => [...prev, { role: 'assistant', content: data.ai_message }]);
      setUserStory(data.user_story || userStory);
      setTurnCount(data.turn_count);
      setSessionStatus(data.session_status);
    } catch (err: any) {
      setDesignError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setChatLoading(false);
      chatInputRef.current?.focus();
    }
  };

  const handleAccept = async () => {
    if (!sessionId) return;
    setChatLoading(true);
    try {
      await api.post(`/api/v1/design-sessions/${sessionId}/accept`);
      setSessionStatus('completed');
      setDesignOpen(false);
    } catch (err: any) {
      setDesignError(err.response?.data?.detail || 'Failed to accept');
    } finally {
      setChatLoading(false);
    }
  };

  const handleAbandon = async () => {
    if (!sessionId) return;
    setChatLoading(true);
    try {
      await api.post(`/api/v1/design-sessions/${sessionId}/abandon`);
      setSessionStatus('abandoned');
      setDesignOpen(false);
    } catch (err: any) {
      setDesignError(err.response?.data?.detail || 'Failed to abandon');
    } finally {
      setChatLoading(false);
    }
  };

  const isActive = sessionStatus === 'active';
  const maxTurns = 10;
  const turnsRemaining = maxTurns - turnCount;

  return (
    <>
      <Fab
        size="medium"
        color="primary"
        onClick={() => setOpen(true)}
        sx={{ position: 'fixed', bottom: { xs: 88, sm: 24 }, right: 24, zIndex: 1300, display: 'flex' }}
        title="Report bug or request feature"
        aria-label="Report bug or request feature"
        data-tour="feedback-fab"
      >
        <Feedback />
      </Fab>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report a Bug or Request a Feature</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <ToggleButtonGroup value={type} exclusive onChange={(_, v) => v && setType(v)} size="small" fullWidth>
            <ToggleButton value="feature">Feature Request</ToggleButton>
            <ToggleButton value="bug">Bug Report</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label="Title"
            placeholder={type === 'bug' ? 'What went wrong?' : 'What would you like?'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth autoFocus
            error={titleTooLong}
            helperText={
              titleTooLong
                ? `Title is too long (${title.length}/${MAX_TITLE_LENGTH} characters)`
                : titleApproachingLimit ? `${title.length}/${MAX_TITLE_LENGTH} characters` : undefined
            }
          />
          <TextField
            label="Details (optional)"
            placeholder="Any extra context..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth multiline rows={3}
          />
          {user?.is_admin && (
            <FormControlLabel
              control={
                <Switch
                  checked={autoFix}
                  onChange={(e) => setAutoFix(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <AutoFixHighIcon sx={{ fontSize: 16, color: autoFix ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: autoFix ? 600 : 400 }}>
                    Auto-fix with Claude Code
                  </Typography>
                </Stack>
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!title.trim() || submitting || titleTooLong}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={showRefine ? 8000 : 3000}
        onClose={() => { setSnackbar(null); setShowRefine(false); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar?.includes('Failed') ? 'error' : 'success'}
          onClose={() => { setSnackbar(null); setShowRefine(false); }}
          action={showRefine ? (
            <Button
              color="inherit" size="small" startIcon={<AutoFixHighIcon />}
              onClick={handleRefineWithAI}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              aria-label="Refine feedback with AI"
              data-tour="refine-with-ai"
            >
              Refine with AI
            </Button>
          ) : undefined}
        >
          {snackbar}
        </Alert>
      </Snackbar>

      {/* Design Session Drawer */}
      <Drawer
        anchor="right"
        open={designOpen}
        onClose={() => setDesignOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 420 }, display: 'flex', flexDirection: 'column' },
          role: 'dialog',
          'aria-label': 'Design Session',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: 16 }}>Refine with AI</Typography>
            {sessionId && <Chip label={`${turnCount}/${maxTurns} turns`} size="small" variant="outlined" sx={{ fontSize: 11 }} />}
          </Stack>
          <IconButton onClick={() => setDesignOpen(false)} aria-label="Close design session" sx={{ minWidth: 44, minHeight: 44 }}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }} role="log" aria-live="polite" aria-label="Design session messages">
          {sessionStarting && <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={28} /></Box>}
          {designError && <Typography variant="body2" color="error" sx={{ mb: 2 }}>{designError}</Typography>}
          {messages.map((msg, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
              <Box sx={{
                maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2,
                backgroundColor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
              }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}>{msg.content}</Typography>
              </Box>
            </Box>
          ))}
          {chatLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
              <Box sx={{ px: 2, py: 1.5, borderRadius: 2, backgroundColor: 'grey.100', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>Thinking...</Typography>
              </Box>
            </Box>
          )}
          {userStory && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Button size="small" onClick={() => setStoryExpanded(!storyExpanded)}
                endIcon={storyExpanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, mb: 0.5 }}
                aria-label={storyExpanded ? 'Collapse user story' : 'Expand user story'}
              >User Story</Button>
              <Collapse in={storyExpanded}>
                <Box sx={{ px: 1.5, py: 1, borderRadius: 1, backgroundColor: 'info.50', border: 1, borderColor: 'info.200', mb: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: 1.6 }}>{userStory}</Typography>
                </Box>
              </Collapse>
            </>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {isActive && sessionId && (
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0 }}>
            <TextField
              inputRef={chatInputRef} value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
              placeholder="Refine your feedback..." multiline maxRows={3} fullWidth size="small"
              disabled={chatLoading || turnsRemaining <= 0} aria-label="Type your message"
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
            <IconButton onClick={handleChatSend} disabled={!chatInput.trim() || chatLoading || turnsRemaining <= 0}
              color="primary" aria-label="Send message" sx={{ minWidth: 44, minHeight: 44 }}>
              <Send />
            </IconButton>
          </Box>
        )}

        {sessionId && (
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
            <Button onClick={handleAbandon} disabled={chatLoading || !isActive} color="inherit" sx={{ minHeight: 44 }} aria-label="Abandon design session">Abandon</Button>
            <Button onClick={handleAccept} disabled={chatLoading || (!isActive && sessionStatus !== 'completed')}
              variant="contained" color="primary" sx={{ minHeight: 44 }} aria-label="Accept design">Accept</Button>
          </Box>
        )}
      </Drawer>
    </>
  );
}
